import { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Checkbox,
  TableSortLabel,
} from "@mui/material";
import { getAdminCodes, generateAdminCodes, revokeAdminCode, deleteAdminCodes } from "../client/admin";

const typeMap = { month: "月卡", year: "年卡", lifetime: "永久" };
const statusMap = { 0: { label: "未使用", color: "success" }, 1: { label: "已使用", color: "default" }, 2: { label: "已作废", color: "error" } };

function CodeManagePage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [genOpen, setGenOpen] = useState(false);
  const [genData, setGenData] = useState({ count: 10, type: "month", prefix: "", note: "", expires_at: "" });
  const [genResult, setGenResult] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [batchDeleteOpen, setBatchDeleteOpen] = useState(false);
  const [sortField, setSortField] = useState("id");
  const [sortOrder, setSortOrder] = useState("desc");

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, search, filterStatus, filterType, sortField, sortOrder]);

  function handleSort(field) {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setPage(0);
  }

  function handleSelectOne(id) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  function handleSelectAll() {
    const selectable = rows.filter((r) => r.status === 0 && r.used_by === null).map((r) => r.id);
    const allSelected = selectable.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !selectable.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...selectable])));
    }
  }

  async function loadData() {
    const params = { page: page + 1, page_size: pageSize, search, sort_field: sortField, sort_order: sortOrder };
    if (filterStatus !== "") params.status = filterStatus;
    if (filterType) params.type = filterType;
    const res = await getAdminCodes(params);
    if (res) {
      setRows(res.list || []);
      setTotal(res.total || 0);
    }
  }

  async function handleGenerate() {
    const res = await generateAdminCodes(genData);
    if (res?.codes) {
      setGenResult(res);
      loadData();
    } else {
      alert(res?.message || "生成失败");
    }
  }

  async function handleRevoke(id) {
    if (!window.confirm("确定作废该激活码？")) return;
    const res = await revokeAdminCode(id);
    if (res?.message === "作废成功") {
      loadData();
    } else {
      alert(res?.message || "作废失败");
    }
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        激活码管理
      </Typography>
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          label="搜索"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && loadData()}
        />
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>状态</InputLabel>
          <Select value={filterStatus} label="状态" onChange={(e) => setFilterStatus(e.target.value)}>
            <MenuItem value="">全部</MenuItem>
            <MenuItem value={0}>未使用</MenuItem>
            <MenuItem value={1}>已使用</MenuItem>
            <MenuItem value={2}>已作废</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>类型</InputLabel>
          <Select value={filterType} label="类型" onChange={(e) => setFilterType(e.target.value)}>
            <MenuItem value="">全部</MenuItem>
            <MenuItem value="month">月卡</MenuItem>
            <MenuItem value="year">年卡</MenuItem>
            <MenuItem value="lifetime">永久</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" onClick={loadData}>搜索</Button>
        <Button variant="outlined" onClick={() => { setGenOpen(true); setGenResult(null); }}>
          批量生成
        </Button>
        {selectedIds.length > 0 && (
          <Button variant="contained" color="error" onClick={() => setBatchDeleteOpen(true)}>
            批量删除 ({selectedIds.length})
          </Button>
        )}
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={
                    selectedIds.length > 0 &&
                    selectedIds.length < rows.filter((r) => r.status === 0 && r.used_by === null).length
                  }
                  checked={
                    rows.filter((r) => r.status === 0 && r.used_by === null).length > 0 &&
                    rows.filter((r) => r.status === 0 && r.used_by === null).every((r) => selectedIds.includes(r.id))
                  }
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>
                <TableSortLabel active={sortField === "id"} direction={sortOrder} onClick={() => handleSort("id")}>
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={sortField === "code"} direction={sortOrder} onClick={() => handleSort("code")}>
                  激活码
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={sortField === "type"} direction={sortOrder} onClick={() => handleSort("type")}>
                  类型
                </TableSortLabel>
              </TableCell>
              <TableCell>批次</TableCell>
              <TableCell>备注</TableCell>
              <TableCell>
                <TableSortLabel active={sortField === "status"} direction={sortOrder} onClick={() => handleSort("status")}>
                  状态
                </TableSortLabel>
              </TableCell>
              <TableCell>使用者</TableCell>
              <TableCell>
                <TableSortLabel active={sortField === "used_at"} direction={sortOrder} onClick={() => handleSort("used_at")}>
                  使用时间
                </TableSortLabel>
              </TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedIds.includes(row.id)}
                    onChange={() => handleSelectOne(row.id)}
                    disabled={row.status !== 0 || row.used_by !== null}
                  />
                </TableCell>
                <TableCell>{row.id}</TableCell>
                <TableCell>
                  <code>{row.code}</code>
                </TableCell>
                <TableCell>{typeMap[row.type] || row.type}</TableCell>
                <TableCell>{row.batch_id || "-"}</TableCell>
                <TableCell>{row.note || "-"}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={statusMap[row.status]?.label || "未知"}
                    color={statusMap[row.status]?.color || "default"}
                  />
                </TableCell>
                <TableCell>{row.used_username || row.used_by || "-"}</TableCell>
                <TableCell>
                  {row.used_at ? new Date(row.used_at).toLocaleString() : "-"}
                </TableCell>
                <TableCell>
                  {row.status === 0 && row.used_by === null && (
                    <Button size="small" color="error" onClick={() => handleRevoke(row.id)}>
                      作废
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={batchDeleteOpen} onClose={() => setBatchDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>确认批量删除</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            确定要删除选中的 {selectedIds.length} 个激活码吗？此操作不可恢复，只能删除未使用的激活码。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchDeleteOpen(false)}>取消</Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              const res = await deleteAdminCodes(selectedIds);
              if (res?.message === "删除成功") {
                setBatchDeleteOpen(false);
                setSelectedIds([]);
                loadData();
              } else {
                alert(res?.message || "删除失败");
              }
            }}
          >
            确认删除
          </Button>
        </DialogActions>
      </Dialog>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 20, 50]}
      />

      <Dialog open={genOpen} onClose={() => setGenOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>批量生成激活码</DialogTitle>
        <DialogContent>
          <TextField
            label="数量"
            type="number"
            fullWidth
            margin="normal"
            size="small"
            value={genData.count}
            onChange={(e) => setGenData({ ...genData, count: parseInt(e.target.value) || 0 })}
            inputProps={{ min: 1, max: 500 }}
          />
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel>类型</InputLabel>
            <Select value={genData.type} label="类型" onChange={(e) => setGenData({ ...genData, type: e.target.value })}>
              <MenuItem value="month">月卡</MenuItem>
              <MenuItem value="year">年卡</MenuItem>
              <MenuItem value="lifetime">永久</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="前缀"
            fullWidth
            margin="normal"
            size="small"
            value={genData.prefix}
            onChange={(e) => setGenData({ ...genData, prefix: e.target.value })}
            helperText="可选，如 FX-"
          />
          <TextField
            label="备注"
            fullWidth
            margin="normal"
            size="small"
            value={genData.note}
            onChange={(e) => setGenData({ ...genData, note: e.target.value })}
          />
          <TextField
            label="激活码有效期"
            type="datetime-local"
            fullWidth
            margin="normal"
            size="small"
            value={genData.expires_at}
            onChange={(e) => setGenData({ ...genData, expires_at: e.target.value })}
            InputLabelProps={{ shrink: true }}
            helperText="激活码本身的有效期，非会员有效期"
          />
          {genResult && (
            <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: "#f5f5f5" }}>
              <Typography variant="subtitle2">生成成功：{genResult.count} 个</Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                批次：{genResult.batch_id}
              </Typography>
              <Box component="pre" sx={{ mt: 1, fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                {genResult.codes.join("\n")}
              </Box>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenOpen(false)}>关闭</Button>
          <Button variant="contained" onClick={handleGenerate}>
            生成
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CodeManagePage;
